import { common } from '@kurtosys/ksys-app-template';
import { IManifest } from '@kurtosys/types/appsManager/IManifest.js';
import { IPagingOptions } from '@kurtosys/types/common/IPagingOptions.js';
import { EventType } from '@kurtosys/types/eventBus/EventType.js';
import { IGlobalSearchInputs } from '@kurtosys/types/eventBus/inputValues/IGlobalSearchInputs.js';
import { IPaginationInputs } from '@kurtosys/types/eventBus/inputValues/IPaginationInputs.js';
import { IPaginationChangeDetail } from '@kurtosys/types/eventBus/IPaginationChangeDetail.js';
import { IUpdatePaginationDetail } from '@kurtosys/types/eventBus/IUpdatePaginationDetail.js';
import { action, computed, makeObservable, observable } from 'mobx';

import { IComponentStylesBase } from '../models/IComponentStylesBase.js';

import { AppStoreBase } from './base/AppStoreBase.js';
import { StoreBase } from './base/StoreBase.js';
import { EventBusStore } from './EventBusStore.js';
import { InputStore } from './InputStore.js';
import { QueryStore } from './QueryStore.js';
import { StoreContext } from './StoreContext.js';

export class PaginationStore<TConfiguration, TComponentStyles extends IComponentStylesBase> extends StoreBase<
	TConfiguration,
	TComponentStyles
> {
	private options: IPagingOptions = {};

	/**
	 * Total number of records in the database relative to the search request filtering.
	 */
	@observable
	public totalRecords = 0;

	/**
	 * Index of first record for the given page, determined by the page size (controlled by the pagination app).
	 * @readonly
	 */
	public get recordIndex(): number {
		return this.options.start || 0;
	}

	/**
	 * Number of records to display per page.
	 * @readonly
	 */
	public get pageSize(): number | undefined {
		return this.options.limit;
	}

	@computed
	private get appStore() {
		return this.storeContext.get<AppStoreBase<TConfiguration, TComponentStyles>>('appStore');
	}

	@computed
	private get inputStore() {
		return this.storeContext.get<InputStore<TConfiguration, TComponentStyles>>('inputStore');
	}

	@computed
	private get eventBusStore() {
		return this.storeContext.get<EventBusStore<TConfiguration, TComponentStyles>>('eventBusStore');
	}

	@computed
	private get queryStore() {
		return this.storeContext.get<QueryStore<TConfiguration, TComponentStyles>>('queryStore');
	}

	constructor(storeContext: StoreContext<TConfiguration, TComponentStyles, any, any, any>, manifest: IManifest) {
		super(storeContext, manifest);
		makeObservable(this);
		const pagingOptions = this.queryStore.getPagingRequest();
		if (pagingOptions?.limit > 0) {
			this.options = pagingOptions;
		} else {
			this.options = {};
		}
	}

	public async initialize() {
		this.appStore.log('error', {
			message: 'Initialize not implemented',
			additionalContext: 'PaginationStore',
		})
	}

	/**
	 * Simply update the static options, doesn't incur state updates,
	 * But is used by the recordIndex when triggering an event to the pagination app.
	 * 
	 * Updates to the API request should be done alongside this change, e.g. Table App > TableStore > sortOnChange().
	 *
	 */
	public reset() {
		this.options.start = 0;
		const inputs: IPaginationInputs = {
			'ksys-pagination-record-index': this.recordIndex,
		};
		this.queryStore.prepareInputsForContext(inputs);
	}

	/**
	 * When an API request returns data which contains pagination details, we should update this store,
	 * and more importantly update the observable total that could be used to drive UI elements.
	 *
	 * Additionally we need to fire off an event to any app that needs to know what the total count is,
	 * Thus this event should only be called as part of the AppStoreBase.initializeConfiguration method 
	 * (before customInitializeAfter is called).
	 * 
	 * Any updates to recordIndex will be catered for via the handlePaginationChangeEvent method.
	 *
	 * @param {IPagingResult} pagingResult
	 */
	@action
	public processApiResponse() {
		const pagingResult = this.queryStore.getPagingResult();
		if (!pagingResult) {
			return;
		}
		const { total, limit, offset } = pagingResult;
		if (this.pageSize === limit) {
			this.totalRecords = total;
			if (!common.commonUtils.isNullOrUndefined(offset) && offset !== this.recordIndex) {
				this.options.start = offset;
			}
			this.triggerUpdate();
			this.totalRecords = total;
		} else {
			this.appStore.log('error', {
				message: 'PaginationStore > unexpected pagination response',
				detail: {
					pagingResult,
					pagingRequestOptions: this.options,
				},
			});
		}
	}

	public triggerUpdate() {
		this.eventBusStore.trigger<IUpdatePaginationDetail>(EventType.updatePagination, {
			detail: {
				identifier: this.appStore.applicationId,
				pagingData: {
					pageSize: this.pageSize,
					recordIndex: this.recordIndex,
					totalRecords: this.totalRecords,
				},
			},
		});
	}

	/**
	 * Handle events from the pagination app, which should in turn update our inputs to refire any
	 * data context API requests dependant on the inputs.
	 *
	 * @param {CustomEvent<IPaginationChangeDetail>} customEvent
	 */
	@action
	public handleChangeEvent = async (customEvent: CustomEvent<IPaginationChangeDetail>) => {
		const { newPagingData } = customEvent.detail;
		const { pageSize, recordIndex } = newPagingData;
		if (pageSize !== this.pageSize) {
			this.resize(pageSize);
		} else if (recordIndex !== this.recordIndex) {
			this.paginate(recordIndex);
		}
	};

	/**
	 * Handle the search term input updates along with the pagination input update,
	 * this should be fired by the AppStoreBase.handleSearchTermUpdate(...) method.
	 *
	 * @param {IGlobalSearchInputs} searchInputValues
	 */
	@action
	public handleSearchTermUpdate = async (searchInputValues: IGlobalSearchInputs) => {
		this.paginate(0, searchInputValues);
	};

	/**
	 * Resize the pagination to use a new record count per page, and by updating the starting page to the 1st record index or page.
	 *
	 * @private
	 * @param {number} pageSize
	 */
	private async resize(pageSize: number) {
		if (pageSize > 0) {
			this.options = { ...this.options, limit: pageSize, start: 0 };
			const inputs: IPaginationInputs = {
				'ksys-pagination-page-size': this.pageSize,
				'ksys-pagination-record-index': this.recordIndex,
			};
			await this.inputStore.updateQueryInputs(inputs);
		}
	}

	/**
	 * Updates the the record index, which used by the API endpoint to fetch a certain page of data,
	 * relative to the page size.
	 *
	 * @private
	 * @param {number} recordIndex
	 */
	private async paginate(recordIndex: number, searchTermValues?: IGlobalSearchInputs) {
		if (recordIndex >= 0) {
			this.options = { ...this.options, start: recordIndex };
			const inputs: IPaginationInputs & IGlobalSearchInputs = {
				...searchTermValues,
				'ksys-pagination-record-index': this.recordIndex,
			};
			await this.inputStore.updateQueryInputs(inputs);
		}
	}
}
